// Client-side port of backend/helpers/text_overlay_math.py color math, so the
// social-template-builder's "Baked preview" shows the EXACT scene-matched colors
// the backend stamps at bake time (text + text-background shapes). KEEP IN
// LOCKSTEP with text_overlay_math.py — the curator validates appearance here
// against the real batch bake.

import type { JsonTemplate } from '@/pages/app/components/social-designs/json-templates/types';

export type ZoneColor = { r: number; g: number; b: number; lum: number };
export type Zone = 'top' | 'bottom' | 'left' | 'right' | 'center' | null;

/** 0-255 RGB → (h 0-360, s 0-1, l 0-1). Mirrors rgb_to_hsl(). */
export function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b);
  const mn = Math.min(r, g, b);
  const lt = (mx + mn) / 2;
  let h = 0;
  let s = 0;
  const d = mx - mn;
  if (d !== 0) {
    s = lt > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
    if (mx === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  return [h, s, lt];
}

/** (h,s,l) → #rrggbb. Mirrors hsl_to_hex(). */
export function hslToHex(h: number, s: number, lt: number): string {
  const c = (1 - Math.abs(2 * lt - 1)) * s;
  const hp = ((((h % 360) + 360) % 360)) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp < 1) { r = c; g = x; b = 0; }
  else if (hp < 2) { r = x; g = c; b = 0; }
  else if (hp < 3) { r = 0; g = c; b = x; }
  else if (hp < 4) { r = 0; g = x; b = c; }
  else if (hp < 5) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }
  const m = lt - c / 2;
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** Legible THEME-TINTED text color. Mirrors derive_text_color(). */
export function deriveTextColor(zc: ZoneColor): string {
  const [h, s] = rgbToHsl(zc.r, zc.g, zc.b);
  if (zc.lum >= 0.5) {
    const sat = Math.min(0.85, Math.max(0.5, s * 1.5 + 0.25));
    return hslToHex(h, sat, 0.22);
  }
  const sat = Math.min(0.4, Math.max(0.08, s * 0.6));
  return hslToHex(h, sat, 0.92);
}

/** Text-BACKGROUND panel color — scene hue, lightness contrasting the text on
 *  it. Mirrors derive_shape_color(). */
export function deriveShapeColor(zc: ZoneColor): string {
  const [h, s] = rgbToHsl(zc.r, zc.g, zc.b);
  if (zc.lum >= 0.5) {
    const sat = Math.min(0.35, Math.max(0.06, s * 0.6));
    return hslToHex(h, sat, 0.90);
  }
  const sat = Math.min(0.55, Math.max(0.1, s * 0.8));
  return hslToHex(h, sat, 0.16);
}

/** Gradient-scrim START color (fades → transparent): the scene's OWN hue,
 *  DARKENED — always dark (so overlaid light text reads) but hue-carrying, not
 *  flat black. Mirrors derive_gradient_color(). */
export function deriveGradientColor(zc: ZoneColor): string {
  const [h, s, lt] = rgbToHsl(zc.r, zc.g, zc.b);
  const sat = Math.min(0.7, Math.max(0.3, s));
  const light = Math.min(0.3, Math.max(0.2, lt * 0.5));
  return hslToHex(h, sat, light);
}

/** Mean color of a pixel region of an ImageData buffer (clamped to bounds). */
function meanRegion(
  data: Uint8ClampedArray, W: number, H: number,
  rx0: number, ry0: number, rx1: number, ry1: number,
): ZoneColor {
  const x0 = Math.max(0, Math.min(W - 1, Math.round(rx0)));
  const y0 = Math.max(0, Math.min(H - 1, Math.round(ry0)));
  const x1 = Math.max(x0 + 1, Math.min(W, Math.round(rx1)));
  const y1 = Math.max(y0 + 1, Math.min(H, Math.round(ry1)));
  let sr = 0;
  let sg = 0;
  let sb = 0;
  let sl = 0;
  let n = 0;
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y * W + x) * 4;
      const R = data[i];
      const G = data[i + 1];
      const B = data[i + 2];
      sr += R; sg += G; sb += B;
      sl += (0.299 * R + 0.587 * G + 0.114 * B) / 255;
      n++;
    }
  }
  if (!n) return { r: 128, g: 128, b: 128, lum: 0.5 };
  return { r: sr / n, g: sg / n, b: sb / n, lum: sl / n };
}

/** Mean color of the named text zone. Mirrors sample_zone_color() crop boxes. */
export function sampleZone(data: Uint8ClampedArray, W: number, H: number, zone: Zone): ZoneColor {
  let x0 = 0;
  let y0 = 0;
  let x1 = W;
  let y1 = H;
  if (zone === 'top') y1 = Math.round(H * 0.4);
  else if (zone === 'bottom') y0 = Math.round(H * 0.6);
  else if (zone === 'left') x1 = Math.round(W * 0.4);
  else if (zone === 'right') x0 = Math.round(W * 0.6);
  else if (zone === 'center') {
    x0 = Math.round(W * 0.25); x1 = Math.round(W * 0.75);
    y0 = Math.round(H * 0.3); y1 = Math.round(H * 0.7);
  }
  return meanRegion(data, W, H, x0, y0, x1, y1);
}

/** Mean color of a shape's region (template coords → image pixels). Mirrors
 *  sample_rect_color(). */
export function sampleRect(
  data: Uint8ClampedArray, W: number, H: number,
  x: number, y: number, w: number, h: number, canvasW: number, canvasH: number,
): ZoneColor {
  const sxw = W / (canvasW || W);
  const syh = H / (canvasH || H);
  return meanRegion(data, W, H, x * sxw, y * syh, (x + w) * sxw, (y + h) * syh);
}

/** Where the template's text sits. Mirrors dominant_text_zone(). */
export function dominantTextZone(template: JsonTemplate): Zone {
  const canvas = template.canvas || ({} as JsonTemplate['canvas']);
  const W = canvas.width || 1024;
  const H = canvas.height || 1024;
  let minX = 1;
  let minY = 1;
  let maxX = 0;
  let maxY = 0;
  let n = 0;
  for (const el of template.elements || []) {
    if (el.type !== 'text') continue;
    n++;
    const x = (el.x as number) || 0;
    const y = (el.y as number) || 0;
    const w = ('width' in el ? (el.width as number) : 0) || 0;
    const rawH = 'height' in el ? (el.height as number | undefined) : undefined;
    const h = rawH == null ? 60 : rawH;
    minX = Math.min(minX, x / W); minY = Math.min(minY, y / H);
    maxX = Math.max(maxX, (x + w) / W); maxY = Math.max(maxY, (y + h) / H);
  }
  if (!n) return null;
  if (maxY - minY > 0.55) return null;
  const vc = (minY + maxY) / 2;
  if (vc < 0.42) return 'top';
  if (vc > 0.58) return 'bottom';
  const hc = (minX + maxX) / 2;
  if (hc < 0.42) return 'left';
  if (hc > 0.58) return 'right';
  return 'center';
}

/** Return a deep clone of `template` with text + text-background shapes colored
 *  from the scene's pixels — the SAME result the backend bake produces. Order
 *  mirrors overlay_bake_compose: text color first, then shape colors. (Fit is
 *  identity for a same-aspect scene, so it's omitted here.) */
export function colorTemplateForScene(
  template: JsonTemplate, data: Uint8ClampedArray, W: number, H: number,
): JsonTemplate {
  const out = structuredClone(template);
  const cw = out.canvas?.width || 1024;
  const ch = out.canvas?.height || 1024;
  const textColor = deriveTextColor(sampleZone(data, W, H, dominantTextZone(out)));

  type AnyEl = Record<string, unknown> & { type?: string; shape?: string; children?: unknown[] };
  const colorEl = (el: AnyEl): void => {
    // A line/rule MATCHES THE TEXT (scene-derived contrast color) and ALWAYS
    // recolors — ignoring lockColor — so it reads as a divider in the headline
    // color, never hardcoded white. Mirrors apply_shape_colors.
    if (el.type === 'shape') {
      const lh = (el.height as number) || 0;
      const isLine =
        el.shape === 'line' ||
        ((el.shape === undefined || el.shape === 'rect') && !el.gradient && lh > 0 && lh <= 6);
      if (isLine) {
        const lw = (el.width as number) || 0;
        if (lw > 0) {
          const zc = sampleRect(data, W, H, (el.x as number) || 0, (el.y as number) || 0, lw, Math.max(lh, 1), cw, ch);
          el.color = deriveTextColor(zc);
        }
        return;
      }
    }
    // Manual color set in the editor — never overwrite with a scene-derived one.
    if (el.lockColor) {
      if (el.type === 'stack' && Array.isArray(el.children)) {
        (el.children as AnyEl[]).forEach(colorEl);
      }
      return;
    }
    if (el.type === 'text') {
      el.color = textColor;
    } else if (el.type === 'shape') {
      const w = (el.width as number) || 0;
      const h = (el.height as number) || 0;
      if (w > 0 && h > 0) {
        const zc = sampleRect(data, W, H, (el.x as number) || 0, (el.y as number) || 0, w, h, cw, ch);
        // Gradient scrims → darkened scene hue (always dark); solid → contrast panel.
        el.color = el.gradient ? deriveGradientColor(zc) : deriveShapeColor(zc);
      }
    } else if (el.type === 'button' && el.outline) {
      // Outline CTA: text + border take the scene-derived text color.
      const w = (el.width as number) || 0;
      const h = (el.height as number) || 0;
      const c = deriveTextColor(
        w > 0 && h > 0
          ? sampleRect(data, W, H, (el.x as number) || 0, (el.y as number) || 0, w, h, cw, ch)
          : sampleZone(data, W, H, null),
      );
      el.textColor = c;
      el.borderColor = c;
    }
    if (el.type === 'stack' && Array.isArray(el.children)) {
      (el.children as AnyEl[]).forEach(colorEl);
    }
  };
  ((out.elements as unknown as AnyEl[]) || []).forEach(colorEl);
  return out;
}
